import Logger from 'pmt-utils/logger'
import { getFromSession, saveToSession } from 'pmt-modules/storage'

/**
 * sessionStorage key used to prevent infinite reload loops when recovering from a
 * chunk load error. Set before reloading; cleared on the next successful page load.
 */
export const CHUNK_ERROR_RELOAD_KEY = 'chunkErrorReloaded'

/**
 * Detects whether an error is a chunk load failure, which typically occurs after a new
 * deployment when the browser tries to load a hashed JS chunk that no longer exists on
 * the server (e.g. index_8f2b9a1c.js replaced by index_91db3a6b.js).
 *
 * @param {*} error
 * @returns {boolean}
 */
export const isChunkLoadError = error => {
  if (!error || typeof error !== 'object') return false
  const message = error?.message || ''
  return (
    message.includes('Loading chunk') ||
    message.includes('ChunkLoadError') ||
    message.includes('Failed to fetch dynamically imported module') ||
    message.includes('Importing a module script failed') ||
    error.name === 'ChunkLoadError'
  )
}

/**
 * Attempts to recover from a chunk load error by reloading the page once.
 *
 * Uses a sessionStorage flag to prevent an infinite reload loop when the error
 * persists after the first reload (e.g. a genuine network failure).
 *
 * @param {*} error - The error to check
 * @param {string} [context] - Optional label appended to the log message for traceability
 * @returns {boolean} true if a reload was triggered, false otherwise
 */
export const handleChunkError = (error, context = '') => {
  if (!isChunkLoadError(error)) return false

  const hasReloaded = getFromSession(CHUNK_ERROR_RELOAD_KEY)
  if (!hasReloaded) {
    const label = context ? ` [${context}]` : ''
    Logger.warn(`Chunk load error detected${label}. Reloading...`, error)
    saveToSession(CHUNK_ERROR_RELOAD_KEY, true)
    window.location.reload()
    return true
  }

  return false
}
